import { useState, useCallback } from 'react';
import { GRID_SIZE } from '../constants/config';

const CELL_STATES = {
  EMPTY: 'EMPTY',
  SUBMITTING: 'SUBMITTING',
  SUBMITTED: 'SUBMITTED',
  RETRYING: 'RETRYING',
  RESUBMITTING: 'RESUBMITTING',
  FINALIZED: 'FINALIZED',
};

const GAME_PHASES = {
  PLAYING: 'PLAYING',
  ALL_FILLED: 'ALL_FILLED',
  SUBMITTING_FINAL: 'SUBMITTING_FINAL',
  RESULTS: 'RESULTS',
  PROTESTING: 'PROTESTING',
  FINAL: 'FINAL',
};

function createEmptyGrid() {
  return Array.from({ length: GRID_SIZE }, (_, row) =>
    Array.from({ length: GRID_SIZE }, (_, col) => ({
      row,
      col,
      attempt1: null,
      attempt2: null,
      finalWord: null,
      finalScore: null,
      explanation: null,
      state: CELL_STATES.EMPTY,
    }))
  );
}

export function useGameState() {
  const [grid, setGrid] = useState(createEmptyGrid);
  const [gamePhase, setGamePhase] = useState(GAME_PHASES.PLAYING);
  const [protestCell, setProtestCell] = useState(null);
  const [protestCount, setProtestCount] = useState(0);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [protestResults, setProtestResults] = useState([]);

  const MAX_RETRIES = 2;

  const updateCell = useCallback((row, col, updates) => {
    setGrid((prev) => {
      const newGrid = prev.map((r) => r.map((c) => ({ ...c })));
      newGrid[row][col] = { ...newGrid[row][col], ...updates };

      const allDone = newGrid.every((r) =>
        r.every((c) => c.state === CELL_STATES.FINALIZED)
      );
      if (allDone) {
        setGamePhase(GAME_PHASES.ALL_FILLED);
      }

      return newGrid;
    });
  }, []);

  const submitAttempt = useCallback(
    (row, col, word, score, explanation, adj1Score, adj2Score) => {
      setGrid((prev) => {
        const newGrid = prev.map((r) => r.map((c) => ({ ...c })));
        const cell = newGrid[row][col];

        if (cell.state === CELL_STATES.SUBMITTING) {
          newGrid[row][col] = {
            ...cell,
            attempt1: { word, score, explanation, adj1Score, adj2Score },
            finalWord: word,
            finalScore: score,
            finalAdj1Score: adj1Score,
            finalAdj2Score: adj2Score,
            explanation,
            state: CELL_STATES.FINALIZED,
          };
        } else if (cell.state === CELL_STATES.RESUBMITTING) {
          const attempt2 = { word, score, explanation, adj1Score, adj2Score };
          const bestScore = Math.max(cell.attempt1.score, score);
          const bestAttempt = score >= cell.attempt1.score ? attempt2 : cell.attempt1;
          const losingAttempt = score >= cell.attempt1.score ? cell.attempt1 : attempt2;
          newGrid[row][col] = {
            ...cell,
            attempt2,
            finalWord: bestAttempt.word,
            finalScore: bestScore,
            finalAdj1Score: bestAttempt.adj1Score,
            finalAdj2Score: bestAttempt.adj2Score,
            explanation: bestAttempt.explanation,
            losingWord: losingAttempt.word,
            state: CELL_STATES.FINALIZED,
          };
        }

        const allFinalized = newGrid.every((r) =>
          r.every((c) => c.state === CELL_STATES.FINALIZED)
        );
        if (allFinalized) {
          setGamePhase(GAME_PHASES.ALL_FILLED);
        }

        return newGrid;
      });
    },
    []
  );

  const startSubmitting = useCallback(
    (row, col) => {
      updateCell(row, col, { state: CELL_STATES.SUBMITTING });
    },
    [updateCell]
  );

  const startResubmitting = useCallback(
    (row, col) => {
      updateCell(row, col, { state: CELL_STATES.RESUBMITTING });
    },
    [updateCell]
  );

  const failSubmitting = useCallback(
    (row, col) => {
      setGrid((prev) => {
        const newGrid = prev.map((r) => r.map((c) => ({ ...c })));
        const cell = newGrid[row][col];
        if (cell.state === CELL_STATES.SUBMITTING) {
          newGrid[row][col] = { ...cell, state: CELL_STATES.EMPTY };
        } else if (cell.state === CELL_STATES.RESUBMITTING) {
          newGrid[row][col] = { ...cell, state: CELL_STATES.RETRYING };
        }
        return newGrid;
      });
    },
    []
  );

  const acceptAttempt = useCallback(
    (row, col) => {
      setGrid((prev) => {
        const newGrid = prev.map((r) => r.map((c) => ({ ...c })));
        const cell = newGrid[row][col];
        newGrid[row][col] = {
          ...cell,
          finalWord: cell.attempt1.word,
          finalScore: cell.attempt1.score,
          finalAdj1Score: cell.attempt1.adj1Score,
          finalAdj2Score: cell.attempt1.adj2Score,
          explanation: cell.attempt1.explanation,
          state: CELL_STATES.FINALIZED,
        };

        const allFinalized = newGrid.every((r) =>
          r.every((c) => c.state === CELL_STATES.FINALIZED)
        );
        if (allFinalized) {
          setGamePhase(GAME_PHASES.ALL_FILLED);
        }

        return newGrid;
      });
    },
    []
  );

  const retryAttempt = useCallback(
    (row, col) => {
      updateCell(row, col, { state: CELL_STATES.RETRYING });
      setRetryCount((c) => c + 1);
    },
    [updateCell]
  );

  const getTotalScore = useCallback(() => {
    return grid.flat().reduce((sum, cell) => sum + (cell.finalScore || 0), 0);
  }, [grid]);

  const submitFinal = useCallback(() => {
    setGamePhase(GAME_PHASES.SUBMITTING_FINAL);
  }, []);

  const showResults = useCallback((data) => {
    setLeaderboardData(data);
    setGamePhase(GAME_PHASES.RESULTS);
  }, []);

  const startProtest = useCallback(
    (row, col) => {
      setProtestCell({ row, col });
      setGamePhase(GAME_PHASES.PROTESTING);
    },
    []
  );

  const resolveProtest = useCallback(
    (accepted, newScore, explanation) => {
      const oldScore = protestCell ? grid[protestCell.row][protestCell.col].finalScore : 0;
      if (accepted && protestCell) {
        const { row, col } = protestCell;
        updateCell(row, col, {
          finalScore: newScore,
          explanation,
        });
      }
      const result = {
        accepted,
        scoreChange: accepted ? newScore - oldScore : 0,
      };
      setProtestResults((prev) => [...prev, result]);
      setProtestCount((c) => c + 1);
      setProtestCell(null);
      setGamePhase(GAME_PHASES.RESULTS);
    },
    [protestCell, updateCell, grid]
  );

  const cancelProtest = useCallback(() => {
    setProtestCell(null);
    setGamePhase(GAME_PHASES.RESULTS);
  }, []);

  const finishGame = useCallback(() => {
    setGamePhase(GAME_PHASES.FINAL);
  }, []);

  const submitEarly = useCallback(() => {
    const newGrid = grid.map((r) =>
      r.map((c) => {
        if (c.state === CELL_STATES.FINALIZED) return { ...c };
        // Auto-accept SUBMITTED cells using their attempt1 data
        if (c.state === CELL_STATES.SUBMITTED && c.attempt1) {
          return {
            ...c,
            finalWord: c.attempt1.word,
            finalScore: c.attempt1.score,
            finalAdj1Score: c.attempt1.adj1Score,
            finalAdj2Score: c.attempt1.adj2Score,
            explanation: c.attempt1.explanation,
            state: CELL_STATES.FINALIZED,
          };
        }
        // Truly empty/in-progress cells get zeroed
        return {
          ...c,
          finalWord: '\u2014',
          finalScore: 0,
          finalAdj1Score: 0,
          finalAdj2Score: 0,
          explanation: null,
          state: CELL_STATES.FINALIZED,
        };
      })
    );
    setGrid(newGrid);
    setGamePhase(GAME_PHASES.ALL_FILLED);
    return newGrid;
  }, [grid]);

  const recoverFromSubmitError = useCallback(() => {
    setGamePhase(GAME_PHASES.ALL_FILLED);
  }, []);

  return {
    grid,
    gamePhase,
    protestCell,
    protestCount,
    leaderboardData,
    retryCount,
    protestResults,
    canRetry: retryCount < MAX_RETRIES,
    updateCell,
    submitAttempt,
    startSubmitting,
    startResubmitting,
    failSubmitting,
    acceptAttempt,
    retryAttempt,
    getTotalScore,
    submitFinal,
    showResults,
    startProtest,
    resolveProtest,
    cancelProtest,
    finishGame,
    submitEarly,
    recoverFromSubmitError,
    CELL_STATES,
    GAME_PHASES,
  };
}

export { CELL_STATES, GAME_PHASES };
